/**
 * SuperAdmin App
 * Main application component with routing
 */

import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useSuperadminAuthStore from './store/authStore';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tenants from './pages/Tenants';
import AuditLog from './pages/AuditLog';
import Settings from './pages/Settings';
import Respaldos from './pages/Respaldos';
import CuentaDetalle from './pages/CuentaDetalle';
import AparienciaSA from './pages/Apariencia';
import Planes from './pages/Planes';
import { ModulosAvanzados, ModuloAvanzadoDetalle } from './pages/ModulosAvanzados';
import Rechazos from './pages/Rechazos';
import Alertas from './pages/Alertas';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, verifyToken } = useSuperadminAuthStore();
  const [checking, setChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const check = async () => {
      await verifyToken();
      setChecking(false);
    };
    check();
  }, [verifyToken]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/superadmin/login" state={{ from: location }} replace />;
  }

  return children;
}

export default function SuperadminApp() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/superadmin/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/superadmin/tenants" element={<Navigate to="/superadmin/cuentas" replace />} />
      <Route path="/superadmin/cuentas" element={<ProtectedRoute><Tenants /></ProtectedRoute>} />
      <Route path="/superadmin/cuentas/:id" element={<ProtectedRoute><CuentaDetalle /></ProtectedRoute>} />
      <Route path="/superadmin/apariencia" element={<ProtectedRoute><AparienciaSA /></ProtectedRoute>} />
      <Route path="/superadmin/planes" element={<ProtectedRoute><Planes /></ProtectedRoute>} />
      <Route path="/superadmin/modulos-avanzados" element={<ProtectedRoute><ModulosAvanzados /></ProtectedRoute>} />
      <Route path="/superadmin/modulos-avanzados/:id" element={<ProtectedRoute><ModuloAvanzadoDetalle /></ProtectedRoute>} />
      <Route path="/superadmin/rechazos" element={<ProtectedRoute><Rechazos /></ProtectedRoute>} />
      <Route path="/superadmin/alertas" element={<ProtectedRoute><Alertas /></ProtectedRoute>} />
      <Route
        path="/superadmin/respaldos"
        element={
          <ProtectedRoute>
            <Respaldos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin/audit"
        element={
          <ProtectedRoute>
            <AuditLog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="/superadmin/*" element={<Navigate to="/superadmin" replace />} />
    </Routes>
  );
}
